<template>
  <section
    :id="sectionId"
    class="campsite-subsection"
    :class="{ desktop: $isDesktop }"
    data-campsite-subsection
  >
    <input
      :id="checkboxId"
      ref="showCheckbox"
      class="show-checkbox"
      type="checkbox"
      aria-hidden="true"
      tabindex="-1"
      @change="showCheckboxChanged"
    />

    <header>
      <h2><slot name="title" /></h2>
      <span class="caret" @click="closeSubsection"
        ><span class="chevron-l" /><span class="chevron-r"
      /></span>
    </header>

    <div class="subsection-content">
      <slot name="contents" />
    </div>

    <slot name="footer">
      <footer
        v-scrollto="{
          target: scrollToId,
          options: { duration: 200, offset: scrollOffset },
        }"
      >
        <label :for="checkboxId" :aria-label="$t('See more...')">
          <span class="caret"
            ><span class="chevron-l" /><span class="chevron-r" /></span
          ><translate class="subsection-close-txt">Close</translate
          ><slot name="subsection-open-txt"
            ><translate
              class="subsection-open-txt"
              :data-sctrack="`${String($route.name)}--CampsiteSubsection-see-more`"
              >See more...</translate
            ></slot
          >
        </label>
      </footer>
    </slot>
  </section>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component
class CampsiteSubsection extends Vue {
  @Prop({ default: false })
  open: boolean

  @Prop({ default: -20 })
  scrollOffset: number

  @Prop({ required: true })
  name: string

  mounted() {
    if (this.open) {
      this.showCheckbox.checked = true
    }
  }

  @Watch('open')
  openChanged(isOpen) {
    this.showCheckbox.checked = isOpen
  }

  showCheckboxChanged() {
    this.$emit('subsection-closed')
  }

  closeSubsection() {
    this.showCheckbox.checked = false
  }

  get showCheckbox() {
    return this.$refs.showCheckbox as HTMLInputElement
  }

  get sectionId() {
    return `${this.name}`
  }

  get scrollToId() {
    return `#${this.sectionId}`
  }

  get checkboxId() {
    return `${this.sectionId.replace(' ', '-')}-show`
  }
}

export default toNative(CampsiteSubsection)
</script>

<style scoped>
.campsite-subsection {
  padding: 1em 1.4em 0;
  position: relative;
  border-bottom: 4px solid var(--colour-page-background);
  overflow: hidden;
}

.subsection-content {
  max-height: 8em;
  position: relative;
}

.show-checkbox {
  position: absolute;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  width: 1px;
  border: 0;
  overflow: hidden;
}

.show-checkbox:checked ~ .subsection-content {
  max-height: none;
}

:deep(.show-checkbox ~ footer .subsection-close-txt) {
  display: none;
}
:deep(.show-checkbox:checked ~ footer .subsection-close-txt) {
  display: inline;
}

:deep(.show-checkbox:checked ~ footer .subsection-open-txt) {
  display: none;
}

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: 0 0 1em 0;
}

h2 {
  font-size: 1.6em;
  font-weight: normal;
  line-height: 1.15;
  margin: 0;
  color: var(--colour-dark-blue);
}

h2 img {
  display: inline-block;
  width: 1.25em;
  height: 1.25em;
}

:deep(footer) {
  padding: 4em 1em 1em;
  background: linear-gradient(
    0deg,
    #fff 0%,
    #fff 35%,
    rgba(255, 255, 255, 0) 100%
  );
  position: relative;
}

:deep(.show-checkbox:checked ~ footer) {
  padding: 1.5em 1em 1em;
  background: transparent;
}

:deep(footer label) {
  font-size: var(--body);
  font-weight: normal;
  color: var(--colour-header-dark-blue);
  margin: 0;
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

:deep(.campsite-subsection.show footer) {
  background: transparent;
  padding: 1.5em 1em 1em;
}

:deep(.caret) {
  display: flex;
  align-items: center;
  width: 1.7em;
  height: 1.7em;
  flex-shrink: 0;
  font-size: 0.65em;
}

header .caret {
  margin: -0.25em 0 0 0;
  font-size: 0.875em;
  display: none;
}

.show-checkbox:checked ~ header .caret {
  display: flex;
}

:deep(footer .caret) {
  margin-right: 0.4em;
}

:deep(.caret span) {
  display: block;
  border-radius: 0.2em;
  height: 0.2em;
  width: 100%;
  transition: transform 0.3s;
  border: 0.1em solid #3a82a6;
  background: #3a82a6;
}

:deep(.chevron-l) {
  transform: rotate(45deg) translate(0.25em, 0);
}

:deep(.chevron-r) {
  transform: rotate(-45deg) translate(-0.25em, 0);
}

.show-checkbox:checked ~ header .caret .chevron-l,
:deep(.show-checkbox:checked ~ footer .caret .chevron-l) {
  transform: rotate(-45deg) translate(0, 0.25em);
}

.show-checkbox:checked ~ header .caret .chevron-r,
:deep(.show-checkbox:checked ~ footer .caret .chevron-r) {
  transform: rotate(45deg) translate(0, 0.25em);
}

:deep(.campsite-subsection#location footer) {
  margin: auto -1.4em;
}

.campsite-subsection.desktop:not(.notices) .subsection-content {
  max-height: none;
}
</style>
